import * as _ from 'lodash';
import { AppFormControlService } from 'ng2-app/modules/borrower/services/app-form-control.service';
import { ApplicationRequirements, ValueRequirement } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.interface';
import { ApplicationRequirementsStore } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.store';
import { Borrower } from 'app/models/dto/borrower.model';
import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import { IgnoreEvent } from './components/app-requirement-collapse/app-requirement-collapse.component';
import { Observable, Subject, ReplaySubject, combineLatest } from 'rxjs';
import { trigger, style, animate, transition } from '@angular/animations';
import { debounceTime, takeUntil, switchMap, map, filter } from 'rxjs/operators';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { SentToFundingDeskModel } from './components/submit-app/submit-app.component';
import { Select } from '@ngxs/store';
import { BorrowerState } from 'app/store/borrower/borrower/borrower.state';
import { notNull } from 'app/modules/shared/functions/filters';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';

export interface AttributeViewState {
  expanded: boolean;
}

@Component({
  selector: 'app-requirements',
  templateUrl: 'app-requirements.component.html',
  styleUrls: ['app-requirements.component.scss'],
  animations: [
    trigger('expandInOut', [
      transition('* => void', [
        style({ height: '*', overflow: 'hidden' }),
        animate(250, style({ height: 0 }))
      ]),
      transition('void => *', [
        style({ height: '0', overflow: 'hidden' }),
        animate(250, style({ height: '*' }))
      ])
    ]),
    trigger('fadeExpandInOut', [
      transition('* => void', [
        style({ opacity: 1, height: '*', overflow: 'hidden' }),
        animate(400, style({ opacity: 0, height: 0 }))
      ]),
      transition('void => *', [
        style({ opacity: 0 }),
        animate(400, style({ opacity: 1 }))
      ])
    ])
  ]
})

export class AppRequirementsComponent implements OnInit, OnDestroy {

  @Input() borrowerId: number;
  @Input() expanded: boolean;
  @Select(BorrowerState.borrower) borrower$: Observable<Borrower>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  canOverrideAppReqs$: Observable<boolean>;
  inAppRequestTest$: Observable<boolean>;
  isValid$ = new ReplaySubject<boolean>(1);
  destroyed$ = new Subject<void>();
  isVisible$: Observable<boolean>;
  requirementsUpdated$ = new Subject<void>();
  sentToFundingDesk$: Observable<boolean>;

  hasWarning: boolean;
  percentComplete: number;
  ignoredExpanded: boolean;
  appRequirements: ApplicationRequirements;
  ignoredRequirements: ValueRequirement[];
  viewState: { [key: string]: AttributeViewState } = {};
  loading: boolean;
  errorTitle: string | null | undefined;
  errorBody: string | null | undefined;
  sentToFundingDesk = false;
  requirementsStatusMessage: string;

  @ViewChild('sentModal', { static: true }) private sentModal: ModalDirective;
  @ViewChild('errorModal', { static: true }) private errorModal: ModalDirective;

  private rawRequirements: ApplicationRequirements;

  constructor(
    private appRequirementsStore: ApplicationRequirementsStore,
    private appFormControlService: AppFormControlService
  ) { }

  ngOnInit() {
    this.percentComplete = 0;
    this.hasWarning = false;
    this.ignoredExpanded = false;

    const inValidStatus$ = this.borrower$.pipe(
      filter(notNull),
      map((borrower) => [
          'noAttempt',
          'attempted',
          'inContact',
          'dealInProgress',
          'docPrep'
        ].includes(borrower.status))
    );

    const canShow$ = combineLatest([
      inValidStatus$,
      this.lenderUser$
    ]).pipe(
      map(([borrowerInValidStatus, lenderUser]) => lenderUser?.permissions.includes('useAppRequirements') && borrowerInValidStatus)
    );

    // Only request and subscribe to the state if the component is canShow$
    this.loading = true;
    canShow$.pipe(
      filter((visible) => visible === true),
      switchMap(() => this.appRequirementsStore.forBorrower(this.borrowerId).pipe(debounceTime(100))),
      takeUntil(this.destroyed$),
    ).subscribe((newRequirements) => {
      this.loading = false;
      this.handleNewRequirements(newRequirements);
    });

    this.canOverrideAppReqs$ = this.lenderUser$.pipe(
      map(currentUser => {
        return currentUser.permissions.includes('overrideAppReqsForSend');
      })
    );

    this.sentToFundingDesk$ = this.borrower$.pipe(
      filter(notNull),
      map((borrower) => borrower.status === 'docPrep')
    );

    this.isVisible$ = combineLatest([
      canShow$,
      this.isValid$,
      this.sentToFundingDesk$
    ]).pipe(
      map(([canShow, isValid, sentToFundingDesk]) => {
        const visible = canShow && (!sentToFundingDesk || (sentToFundingDesk && !isValid));
        return visible;
      })
    );

    combineLatest([
      this.borrower$,
      this.isValid$,
    ]).pipe(takeUntil(this.destroyed$)).subscribe(([borrower, isValid]) => {
      if(borrower){
        switch(borrower?.status){
          case 'noAttempt':
          case 'attempted':
          {
            if(isValid){
              this.requirementsStatusMessage = 'To Submit app to Funding Desk, borrower status needs to be \'In Contact\'';
            }else{
              this.requirementsStatusMessage = `To Submit app to Funding Desk, borrower status needs to be 'In Contact'
                                                and all necessary requirements need to be complete.`;
            }
          }
          break;
          case 'inContact':
          {
            if(isValid){
              this.requirementsStatusMessage = 'All requirements met, ready to submit.';
            }else{
              this.requirementsStatusMessage = 'To Submit app to Funding Desk all necessary requirements need to be complete.';
            }
          }
          break;
          case 'docPrep':
          {
            if(isValid){
              this.requirementsStatusMessage = `Application has recently been submitted to Funding Desk.
                                                To re-perform action borrower status needs to be 'In Contact'.`;
            }else{
              this.requirementsStatusMessage = `Application has recently been submitted to Funding Desk.
                                                To re-perform action borrower status needs to be 'In Contact'
                                                and all necessary requirements need to be complete.`;
            }
          }
          break;
          case 'dealInProgress':
          {
            this.requirementsStatusMessage = 'Deals have been created and are in progress.';
          }
          break;
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.expanded = false;
  }

  get showBankRequirement() {
    return this.appRequirements
      && this.appRequirements.bankStatementRequirement
      && this.appRequirements.bankStatementRequirement.months
      && this.appRequirements.bankStatementRequirement.months.length > 0;
  }

  reqTrackId(x, requirement: ValueRequirement) {
    return requirement.key;
  }

  toggleExpandedIgnored() {
    this.ignoredExpanded = !this.ignoredExpanded;
  }

  /**
   * Optimistically remove the bank statement requirement client-side
   *
   * @param event
   */
  onBankRequirementMet() {
    const updatedRequirements = Object.assign({}, this.rawRequirements);
    delete updatedRequirements.bankStatementRequirement;
    this.handleNewRequirements(updatedRequirements);
  }

  /**
   * Optimistically toggle requirement ignored state client-side
   *
   * @param event
   */
  handleIgnoreEvent(event: IgnoreEvent) {
    const updatedRequirements = Object.assign({}, this.rawRequirements);

    if (!updatedRequirements.valueRequirements) {
      return;
    }

    // Find the requirement
    const req = updatedRequirements.valueRequirements.find((r) => r.issues.findIndex((i) => i.requirementId === event.requirementId) >= 0);
    if (!req) {
      return;
    }

    // Find the issue of the requirement
    const issue = req.issues.find((i) => i.requirementId === event.requirementId);
    if (!issue) {
      return;
    }

    // Update the requirements
    issue.ignored = event.ignored;
    req.status = req.issues.some(i => !i.ignored) ? 'warning' : 'ignored';
    this.handleNewRequirements(updatedRequirements);
  }

  submitted(event: SentToFundingDeskModel): void {
    if (event.sent) {
      this.sentModal.show();
      this.sentToFundingDesk = true;
    } else {
      this.errorTitle = event.modelErrorTitle;
      this.errorBody = event.modelErrorBody;
      this.errorModal.show();
    }
    this.expanded = false;
  }

  private handleNewRequirements(newRequirements: ApplicationRequirements) {
    this.rawRequirements = newRequirements;
    const valueRequirements: ValueRequirement[] = _.get(newRequirements, 'valueRequirements', []);
    const ignoredRequirements: ValueRequirement[] = valueRequirements.filter(req => req.status === 'ignored');
    const unIgnoredRequirements: ValueRequirement[] = valueRequirements.filter(req => req.status !== 'ignored');
    this.ignoredRequirements = ignoredRequirements;

    this.appRequirements = {
      bankStatementRequirement: newRequirements.bankStatementRequirement,
      metRequirements: newRequirements.metRequirements,
      valueRequirements: _.sortBy(unIgnoredRequirements, 'status')
    };

    this.calculatePercentComplete(unIgnoredRequirements, newRequirements, ignoredRequirements);
    this.calculateHasWarning(valueRequirements);

    if (this.percentComplete < 100) {
      this.expanded = true;
    }

    this.isValid$.next(!this.hasWarning && this.percentComplete >= 100);

    // Ensure form controls are updated with new values after the DOM is updated
    setTimeout(() => this.appFormControlService.updateForms());
  }

  private calculateHasWarning(valueRequirements: ValueRequirement[]) {
    this.hasWarning = valueRequirements.reduce((accumulator: boolean, iteratee: object) => {
      if (accumulator) {
        return accumulator;
      }
      // If there's already a warning, no need to check anymore
      if (iteratee['status'] === 'warning') {
        accumulator = true;
      }
      return accumulator;
    }, false);
  }

  private calculatePercentComplete(
    unIgnoredRequirements: ValueRequirement[],
    newRequirements: ApplicationRequirements,
    ignoredRequirements: ValueRequirement[]
  ) {
    const numUnIgnoredRequirements: number = unIgnoredRequirements.length;
    const numMetRequirements: number = _.get(newRequirements, 'metRequirements', 0);
    const numIgnoredRequirements: number = ignoredRequirements.length;
    const bankRequirement: number = this.isStatementRequirementMet() ? 0 : 1;
    const allRequirements = numIgnoredRequirements + numMetRequirements + numUnIgnoredRequirements + bankRequirement;
    this.percentComplete = (numIgnoredRequirements + numMetRequirements) / allRequirements * 100 || 0;
  }

  private isStatementRequirementMet() {
    if (!this.appRequirements.bankStatementRequirement) {
      return true;
    }

    const anyUnsentOrErrored = this.appRequirements.bankStatementRequirement.months
      .filter((m) => !['processing', 'complete'].includes(m.status)).length > 0;

    if (anyUnsentOrErrored) {
      return false;
    }

    return true;
  }
}
